import { useCallback, useEffect, useState } from "react";
import { Outlet, useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { TabNav } from "@/components/TabNav";
import { useApi } from "@/lib/api";
import type { PagedResult } from "@/lib/pagination";
import { EntityDetailHeader } from "./EntityDetailHeader";

interface Connector {
  id: string;
  name: string;
  lastSeenAt: string | null;
  platform: string | null;
}

export interface ConnectorLayoutContext {
  onConnectorRenamed: (name: string) => void;
  resourceCount: number;
}

export function ConnectorLayout() {
  const { accessToken } = useAuth();
  const api = useApi();
  const { orgId, connectorId } = useParams();
  const [connector, setConnector] = useState<Connector | null>(null);
  const [resourceCount, setResourceCount] = useState<number | undefined>(
    undefined,
  );

  const load = useCallback(async () => {
    if (!accessToken || !orgId || !connectorId) {
      return null;
    }

    const [data, resources] = await Promise.all([
      api.get<Connector>(`/organizations/${orgId}/connectors/${connectorId}`),
      api.get<PagedResult<unknown>>(
        `/organizations/${orgId}/connectors/${connectorId}/resources?page=1&pageSize=1`,
      ),
    ]);

    return { connector: data, resourceCount: resources?.totalCount };
  }, [accessToken, api, orgId, connectorId]);

  useEffect(() => {
    let cancelled = false;

    void load().then((result) => {
      if (!cancelled && result) {
        setConnector(result.connector);
        setResourceCount(result.resourceCount);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [load]);

  function onConnectorRenamed(name: string) {
    setConnector((current) => (current ? { ...current, name } : current));
  }

  return (
    <div className="mx-auto max-w-3xl text-foreground">
      <EntityDetailHeader
        backTo={`/${orgId}/networking/connectors`}
        backLabel="Connectors"
        name={connector?.name ?? null}
        lastSeenAt={connector?.lastSeenAt ?? null}
        facts={[connector?.platform ?? null]}
      />

      <TabNav
        tabs={[
          { label: "Details", to: "details" },
          { label: "Resources", to: "resources", count: resourceCount },
          { label: "Settings", to: "settings" },
        ]}
        basePath={`/${orgId}/networking/connectors/${connectorId}`}
      />

      <Outlet
        context={
          {
            onConnectorRenamed,
            resourceCount: resourceCount ?? 0,
          } satisfies ConnectorLayoutContext
        }
      />
    </div>
  );
}
